// Helpers to turn a list of "my dives" (launch + log entries) into log-book friendly
// text snippets that divers can paste into their PADI / SSI / NAUI / ISC apps or a
// paper log book.
//
// `dives` shape:
//   { date, time, site, depth_m, water_temp_c, duration_min, buddy, instructor, trip_name, launch_label, certification }

const escape = (s) => (s == null ? '' : String(s));

const fmtDive = (d, idx) => ({
  ...d,
  no: idx + 1,
  depth: d.depth_m != null ? `${d.depth_m} m` : '—',
  temp: d.water_temp_c != null ? `${d.water_temp_c} °C` : '—',
  duration: d.duration_min != null ? `${d.duration_min} min` : '—',
});

const padiBlock = (d) => `Dive ${d.no}
  Date: ${escape(d.date)}
  Time: ${escape(d.time)}
  Location: ${escape(d.site)} (${escape(d.trip_name)} — ${escape(d.launch_label)})
  Max Depth: ${d.depth}
  Bottom Time: ${d.duration}
  Water Temp: ${d.temp}
  Buddy: ${escape(d.buddy)}
  Verified by: ${escape(d.instructor)}
`;

const ssiBlock = (d) => `SSI Dive #${d.no}
  Date: ${escape(d.date)} ${escape(d.time)}
  Dive Site: ${escape(d.site)}
  Trip: ${escape(d.trip_name)} · ${escape(d.launch_label)}
  Max Depth: ${d.depth}
  Run Time: ${d.duration}
  Water Temp: ${d.temp}
  Buddy: ${escape(d.buddy)}
  Recorded by: ${escape(d.instructor)} (SSI instructor reference only)
`;

const nauiBlock = (d) => `NAUI Dive Log Entry ${d.no}
  Date: ${escape(d.date)} | Time In: ${escape(d.time)}
  Site: ${escape(d.site)} (${escape(d.trip_name)})
  Launch: ${escape(d.launch_label)}
  Depth: ${d.depth}    Duration: ${d.duration}
  Water Temp: ${d.temp}
  Buddy: ${escape(d.buddy)}
  Witness: ${escape(d.instructor)}
`;

const iscBlock = (d) => `ISC Dive ${d.no}
  Date / Time: ${escape(d.date)} ${escape(d.time)}
  Dive Site: ${escape(d.site)}
  Trip: ${escape(d.trip_name)} · ${escape(d.launch_label)}
  Max Depth: ${d.depth}
  Bottom Time: ${d.duration}
  Water Temp: ${d.temp}
  Buddy: ${escape(d.buddy)}
  Logged by: ${escape(d.instructor)} (ISC reference)
`;

const plainBlock = (d) => `Dive ${d.no}
  When: ${escape(d.date)} ${escape(d.time)}
  Where: ${escape(d.site)} (${escape(d.trip_name)} — ${escape(d.launch_label)})
  Depth: ${d.depth}, Duration: ${d.duration}, Temp: ${d.temp}
  Buddy: ${escape(d.buddy)}
  Recorded by: ${escape(d.instructor)}
`;

const HEADER = (diverName, format) => {
  const tag = format === 'plain' ? 'Dive log' : `${format.toUpperCase()} dive log`;
  return `${tag} — ${diverName}\n${'-'.repeat(40)}\n\nNOTE: The depth, time and temperature below were recorded by the dive operator's instructor / dive master. They are provided for reference only — please confirm against your own dive computer where applicable.\n\n`;
};

const FORMATS = [
  { id: 'padi', label: 'PADI', fn: padiBlock },
  { id: 'ssi',  label: 'SSI',  fn: ssiBlock },
  { id: 'naui', label: 'NAUI', fn: nauiBlock },
  { id: 'isc',  label: 'ISC',  fn: iscBlock },
  { id: 'plain', label: 'Plain text', fn: plainBlock },
];

export const formatDives = (diverName, dives, formatId = 'padi') => {
  const fmt = FORMATS.find((f) => f.id === formatId) || FORMATS[0];
  const enriched = dives.map(fmtDive);
  return HEADER(diverName, fmt.id) + enriched.map(fmt.fn).join('\n');
};

export const LOG_FORMATS = FORMATS.map(({ id, label }) => ({ id, label }));
